(() => {
  'use strict';

  if (window.NovaCartLimitedOffer?.init) {
    window.NovaCartLimitedOffer.init(document);
    return;
  }

  const routesRoot = window.Shopify?.routes?.root || '/';
  const select = (selector, context = document) => context.querySelector(selector);
  const selectAll = (selector, context = document) => Array.from(context.querySelectorAll(selector));

  const formatMoney = (cents, currency) => {
    const amount = Number(cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-PK', {
        style: 'currency',
        currency: currency || 'PKR',
        currencyDisplay: 'code',
        maximumFractionDigits: 0
      }).format(amount);
    } catch (error) {
      return `PKR ${Math.round(amount).toLocaleString()}`;
    }
  };

  const sameOptions = (left = [], right = []) => left.length === right.length && left.every((value, index) => String(value) === String(right[index]));

  const setStatus = (root, message = '', type = 'success') => {
    const status = select('[data-limited-offer-status]', root);
    if (!status) return;
    status.textContent = message;
    status.classList.toggle('is-error', type === 'error');
  };

  const findVariant = (state, id) => state.data.variants.find((variant) => String(variant.id) === String(id));

  const updateSummary = (root, state) => {
    const rows = selectAll('[data-offer-item]', root);
    const selectedVariants = rows.map((row) => findVariant(state, row.dataset.variantId)).filter(Boolean);
    const isComplete = selectedVariants.length === state.quantity && selectedVariants.every((variant) => variant.available);
    const subtotal = selectedVariants.reduce((sum, variant) => sum + Number(variant.price || 0), 0);
    const savings = Math.round(subtotal * state.discount / 100);
    const total = Math.max(0, subtotal - savings);

    const subtotalNode = select('[data-limited-offer-subtotal]', root);
    const savingsNode = select('[data-limited-offer-savings]', root);
    const totalNode = select('[data-limited-offer-total]', root);
    const tierMessage = select('[data-limited-offer-tier-message]', root);
    const submit = select('[data-limited-offer-submit]', root);
    if (subtotalNode) subtotalNode.textContent = formatMoney(subtotal, state.data.currency);
    if (savingsNode) savingsNode.textContent = `−${formatMoney(savings, state.data.currency)}`;
    if (totalNode) totalNode.textContent = formatMoney(total, state.data.currency);
    if (tierMessage) tierMessage.textContent = `Buy ${state.quantity} and save ${state.discount}% automatically.`;
    if (submit) {
      submit.disabled = !isComplete;
      const label = select('span', submit);
      if (label) label.textContent = `Build Your Bundle — Save ${state.discount}%`;
    }
  };

  const updateRowVariant = (root, state, row) => {
    const selectedValues = selectAll('[data-offer-option]', row).map((node) => node.value);
    const variant = state.data.variants.find((candidate) => sameOptions(candidate.options, selectedValues));
    const image = select('img', row);
    const availability = select('[data-offer-availability]', row);
    const price = select('[data-offer-item-price]', row);

    if (!variant || !variant.available) {
      row.dataset.variantId = '';
      row.classList.add('is-unavailable');
      if (availability) availability.textContent = variant ? 'Sold out' : 'Unavailable combination';
      if (price) price.textContent = 'Unavailable';
      if (variant?.image && image) image.src = variant.image;
    } else {
      row.dataset.variantId = String(variant.id);
      row.classList.remove('is-unavailable');
      if (availability) availability.textContent = 'Available';
      if (price) price.textContent = formatMoney(variant.price, state.data.currency);
      if (image) {
        image.src = variant.image || state.data.fallbackImage || '';
        image.hidden = !image.src;
      }
    }
    updateSummary(root, state);
  };

  const createItemRow = (root, state, index, preferredVariantId) => {
    const availableVariants = state.data.variants.filter((variant) => variant.available);
    const preferred = findVariant(state, preferredVariantId);
    const variant = preferred?.available ? preferred : availableVariants[0] || state.data.variants[0];
    const row = document.createElement('div');
    row.className = 'limited-offer__item';
    row.setAttribute('data-offer-item', '');
    row.innerHTML = `
      <span class="limited-offer__item-number" aria-hidden="true">${index + 1}</span>
      <span class="limited-offer__item-media"><img loading="lazy"></span>
      <span class="limited-offer__item-meta"><strong data-offer-item-title></strong><small data-offer-availability>Available</small></span>
      <span class="limited-offer__option-grid" data-offer-options></span>
      <span class="limited-offer__item-qty"><span>Qty</span><strong>1</strong></span>
      <strong class="limited-offer__item-price" data-offer-item-price></strong>`;

    const title = select('[data-offer-item-title]', row);
    if (title) title.textContent = `Item ${index + 1}`;
    const image = select('img', row);
    if (image) {
      image.alt = `${state.data.title} — Item ${index + 1}`;
      image.src = variant?.image || state.data.fallbackImage || '';
      image.hidden = !image.src;
    }

    const optionsContainer = select('[data-offer-options]', row);
    state.data.options.forEach((option, optionIndex) => {
      const label = document.createElement('label');
      label.className = 'limited-offer__option';
      if (state.data.options.length === 1 && option.values.length === 1 && String(option.name).toLowerCase() === 'title') label.classList.add('is-hidden');
      const caption = document.createElement('span');
      caption.textContent = option.name;
      const optionSelect = document.createElement('select');
      optionSelect.className = 'limited-offer__select';
      optionSelect.setAttribute('data-offer-option', '');
      optionSelect.setAttribute('aria-label', `${option.name} for Item ${index + 1}`);
      option.values.forEach((value) => {
        const optionNode = document.createElement('option');
        optionNode.value = value;
        optionNode.textContent = value;
        optionSelect.appendChild(optionNode);
      });
      optionSelect.value = variant?.options?.[optionIndex] || option.values[0] || '';
      optionSelect.addEventListener('change', () => updateRowVariant(root, state, row));
      label.append(caption, optionSelect);
      optionsContainer?.appendChild(label);
    });

    row.dataset.variantId = variant?.available ? String(variant.id) : '';
    updateRowVariant(root, state, row);
    return row;
  };

  const renderItems = (root, state) => {
    const container = select('[data-limited-offer-items]', root);
    if (!container) return;
    const previousVariantIds = selectAll('[data-offer-item]', root).map((row) => row.dataset.variantId);
    container.innerHTML = '';
    for (let index = 0; index < state.quantity; index += 1) {
      container.appendChild(createItemRow(root, state, index, previousVariantIds[index] || state.data.selectedVariantId));
    }
    updateSummary(root, state);
  };

  const addBundle = async (root, state) => {
    const rows = selectAll('[data-offer-item]', root);
    const variants = rows.map((row) => findVariant(state, row.dataset.variantId));
    if (variants.length !== state.quantity || variants.some((variant) => !variant?.available)) {
      setStatus(root, 'Choose an available variant for every bundle item.', 'error');
      return;
    }

    const quantities = new Map();
    variants.forEach((variant) => quantities.set(String(variant.id), (quantities.get(String(variant.id)) || 0) + 1));
    const items = Array.from(quantities.entries()).map(([id, quantity]) => ({
      id,
      quantity,
      properties: { '_Limited time offer': `Buy ${state.quantity} — ${state.discount}% off` }
    }));

    const submit = select('[data-limited-offer-submit]', root);
    const submitLabel = select('span', submit);
    const originalLabel = submitLabel?.textContent || '';
    if (submit) submit.disabled = true;
    if (submitLabel) submitLabel.textContent = 'Building your bundle…';
    setStatus(root, 'Adding your selected variants to the cart…');

    try {
      const response = await fetch(`${routesRoot}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.description || result.message || 'Unable to add this bundle.');
      setStatus(root, 'Bundle added. Opening your cart…');
      document.dispatchEvent(new CustomEvent('novacart:cart-updated', { detail: result }));
      window.location.assign(`${routesRoot}cart`);
    } catch (error) {
      setStatus(root, error.message || 'Unable to add this bundle. Please try again.', 'error');
      if (submit) submit.disabled = false;
      if (submitLabel) submitLabel.textContent = originalLabel;
    }
  };

  const initRoot = (root) => {
    if (!root || root.dataset.limitedOfferInitialized === 'true') return;
    const dataNode = select('[data-limited-offer-product]', root);
    if (!dataNode) return;
    let data;
    try {
      data = JSON.parse(dataNode.textContent);
    } catch (error) {
      return;
    }
    if (!Array.isArray(data.variants) || !data.variants.some((variant) => variant.available)) return;

    root.dataset.limitedOfferInitialized = 'true';
    const state = { data, quantity: 2, discount: 10 };
    selectAll('[data-offer-tier]', root).forEach((button) => {
      button.addEventListener('click', () => {
        state.quantity = Number(button.dataset.offerTier || 2);
        state.discount = Number(button.dataset.offerDiscount || 0);
        selectAll('[data-offer-tier]', root).forEach((candidate) => {
          const active = candidate === button;
          candidate.classList.toggle('is-active', active);
          candidate.setAttribute('aria-pressed', String(active));
        });
        setStatus(root, '');
        renderItems(root, state);
      });
    });
    select('[data-limited-offer-submit]', root)?.addEventListener('click', () => addBundle(root, state));
    renderItems(root, state);
  };

  const init = (context = document) => {
    if (context.matches?.('[data-limited-offer]')) initRoot(context);
    selectAll('[data-limited-offer]', context).forEach(initRoot);
  };

  window.NovaCartLimitedOffer = { init };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  else init(document);
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
